import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CFormInput,
  CRow,
  CSpinner,
  CTooltip,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilInfo, cilMinus, cilPlus } from '@coreui/icons'

import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { buildApiUrl } from '../../utils/getConfiguration'
import WidgetSetAOI from '../widgets/WidgetSetAOI'
//import aoiPic1 from 'src/assets/devices/house.jpg'
//import aoiPic2 from 'src/assets/devices/flat.jpg'
//import aoiPic3 from 'src/assets/devices/car.jpeg'
//import aoiPic4 from 'src/assets/devices/health.jpeg'
//import aoiPic1000 from 'src/assets/devices/manager.jpeg'
//import aoiPic1001 from 'src/assets/devices/innovator.jpeg'
import { AOI_FULL_CONFIG, profileModules } from '../../utils/getConfiguration'
import { SplashScreen } from '@capacitor/splash-screen'
import InternalAppHeader from './components/InternalAppHeader'
import { Trans, useTranslation } from 'react-i18next'

const CATEGORY_ORDER_KEYS = ['personal', 'business', 'other']
const AOI_FILTER_IDS = ['all', 'active', 'inactive']

const Settings = () => {
  const [timerHours, setTimerHours] = useState(
    () => Number(localStorage.getItem('timerhours')) || 0,
  )
  const [timerMinutes, setTimerMinutes] = useState(
    () => Number(localStorage.getItem('timerminutes')) || 0,
  )
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [aoiFilter, setAoiFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const { t } = useTranslation()

  const filterOptions = useMemo(
    () =>
      AOI_FILTER_IDS.map((id) => ({
        id,
        label: t(`settings.areas.filters.${id}`),
      })),
    [t],
  )

  const fetchAccountsData = useCallback(
    async ({ showSpinner = false } = {}) => {
      try {
        if (showSpinner) {
          setLoading(true)
        }
        const mail = localStorage.getItem('mail')
        const response = await apiClient.get(
          buildApiUrl('account', {
            email: mail,
          }),
        )
        setAccounts(response.data)
        setTimerHours(Number(localStorage.getItem('timerhours')) || 0)
        setTimerMinutes(Number(localStorage.getItem('timerminutes')) || 0)
      } catch (error) {
        setError(t('settings.errors.fetchAccounts'))
        console.error('Error fetching account data', error)
      } finally {
        if (showSpinner) {
          setLoading(false)
        }
      }
    },
    [t],
  )
  const handleRefresh = useCallback(() => {
    fetchAccountsData()
  }, [fetchAccountsData])

  useEffect(() => {
    fetchAccountsData({ showSpinner: true })
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    if (window?.SplashScreen?.hide) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((splashError) => {
        console.warn('Unable to hide splash screen on Settings load', splashError)
      })
    }
  }, [fetchAccountsData])

  const reminderSet = useCallback(
    async (hours, minutes) => {
      const account_ID = localStorage.getItem('account_ID')
      if (!account_ID) {
        console.warn('Missing account_ID; reminder settings were not updated.')
        return
      }

      localStorage.setItem('timerhours', String(hours))
      localStorage.setItem('timerminutes', String(minutes))
      try {
        await apiClient.get(
          buildApiUrl('accountsReminderUpdate', {
            account_ID,
            hours: String(hours),
            minutes: String(minutes),
          }),
        )
        console.log('Reminder settings updated')
      } catch (error) {
        console.error('Failed to persist reminder settings:', error)
        throw error
      } finally {
        await fetchAccountsData()
      }
    },
    [fetchAccountsData],
  )

  const persistReminder = useCallback(
    (hours, minutes) => {
      reminderSet(hours, minutes).catch((error) => {
        console.error('Reminder refresh failed:', error)
      })
    },
    [reminderSet],
  )

  const normalizedAOI = useMemo(() => {
    return AOI_FULL_CONFIG.map((aoi) => {
      const img = aoi.img ?? aoi.image
      const jsonField = aoi.JSONfield ?? `aoi${aoi.grp_ID}`
      const label = aoi.labelKey ? t(aoi.labelKey) : (aoi.label ?? aoi.key)
      return {
        key: aoi.grp_ID,
        label,
        img,
        aoiKey: jsonField,
        ageKey: `${jsonField}_age`,
        configEndpoint: aoi.configEndpoint,
        ageEndpoint: aoi.ageEndpoint,
        aoiName: aoi.key,
      }
    })
  }, [t])

  const overallSummary = useMemo(() => {
    if (!accounts?.length) {
      return {
        enabled: 0,
        disabled: 0,
        unknown: normalizedAOI.length,
      }
    }

    return accounts.reduce(
      (totals, account) => {
        normalizedAOI.forEach((aoi) => {
          const rawStatus = account?.[aoi.aoiKey]
          const normalizedStatus =
            rawStatus === null || rawStatus === undefined
              ? 'unknown'
              : String(rawStatus).toUpperCase()

          if (normalizedStatus === 'Y') totals.enabled += 1
          else if (normalizedStatus === 'N') totals.disabled += 1
          else totals.unknown += 1
        })
        return totals
      },
      { enabled: 0, disabled: 0, unknown: 0 },
    )
  }, [accounts, normalizedAOI])

  const decrementHours = useCallback(() => {
    const next = (timerHours + 23) % 24
    setTimerHours(next)
    persistReminder(next, timerMinutes)
  }, [persistReminder, timerHours, timerMinutes])

  const incrementHours = useCallback(() => {
    const next = (timerHours + 1) % 24
    setTimerHours(next)
    persistReminder(next, timerMinutes)
  }, [persistReminder, timerHours, timerMinutes])

  const decrementMinutes = useCallback(() => {
    const next = (timerMinutes + 59) % 60
    setTimerMinutes(next)
    persistReminder(timerHours, next)
  }, [persistReminder, timerHours, timerMinutes])

  const incrementMinutes = useCallback(() => {
    const next = (timerMinutes + 1) % 60
    setTimerMinutes(next)
    persistReminder(timerHours, next)
  }, [persistReminder, timerHours, timerMinutes])

  const navigate = useNavigate()

  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader
          title={t('settings.header.title')}
          onBack={() => navigate('/dashboard', { state: { refresh: true } })}
        />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10} className="d-flex flex-column gap-4">
            {error && (
              <CAlert color="danger" dismissible className="mb-0" onClose={() => setError(null)}>
                {error}
              </CAlert>
            )}

            <CCard className="border-0 shadow-sm">
              <CCardBody className="p-4 p-lg-5 d-flex flex-column gap-4">
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <CSpinner
                      color="primary"
                      variant="grow"
                      style={{ width: '3rem', height: '3rem' }}
                      aria-label={t('settings.loading.aria')}
                    />
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <h5 className="mb-0">{t('settings.overview.title')}</h5>
                      <CBadge color="info" className="text-uppercase px-3 py-2">
                        {t('settings.overview.activeModules', {
                          count: overallSummary.enabled,
                        })}
                      </CBadge>
                    </div>

                    {accounts.length > 0 ? (
                      accounts.map((account, index) => {
                        const accountTitle =
                          account?.account_name ??
                          account?.name ??
                          account?.AccountName ??
                          t('settings.overview.accountFallback')
                        const accountKey =
                          account?.id ?? account?.account_ID ?? account?.email ?? index
                        const normalizedSearchTerm = searchTerm.trim().toLowerCase()
                        let anyVisibleCategory = false

                        const aoiWithStatus = normalizedAOI.map((aoi) => {
                          const rawStatus = account?.[aoi.aoiKey]
                          const normalizedStatus =
                            rawStatus === null || rawStatus === undefined
                              ? 'unknown'
                              : String(rawStatus).toUpperCase()
                          const status =
                            normalizedStatus === 'Y'
                              ? 'enabled'
                              : normalizedStatus === 'N'
                                ? 'disabled'
                                : 'unknown'

                          const profileEntry = Object.entries(profileModules).find(([, modules]) =>
                            modules.includes(aoi.aoiName),
                          )
                          const categoryKey = profileEntry?.[0] ?? 'other'

                          return {
                            ...aoi,
                            status,
                            categoryKey,
                          }
                        })

                        const summary = aoiWithStatus.reduce(
                          (acc, item) => {
                            if (item.status === 'enabled') acc.enabled += 1
                            else if (item.status === 'disabled') acc.disabled += 1
                            else acc.unknown += 1
                            return acc
                          },
                          { enabled: 0, disabled: 0, unknown: 0 },
                        )

                        const groupedByCategory = aoiWithStatus.reduce((acc, item) => {
                          if (!acc[item.categoryKey]) {
                            acc[item.categoryKey] = []
                          }
                          acc[item.categoryKey].push(item)
                          return acc
                        }, {})

                        const categoryOrder = [
                          ...CATEGORY_ORDER_KEYS,
                          ...Object.keys(groupedByCategory).filter(
                            (category) => !CATEGORY_ORDER_KEYS.includes(category),
                          ),
                        ]

                        return (
                          <CCard key={accountKey} className="mb-4 border-0 shadow-sm">
                            <CCardBody>
                              <CRow className="align-items-center justify-content-between mb-4 g-3">
                                <CCol xs={12} md="auto">
                                  <h5 className="mb-1">{accountTitle}</h5>
                                </CCol>
                                {account?.email && (
                                  <CCol xs={12} md="auto" className="text-md-end">
                                    <span className="text-uppercase small text-muted d-block">
                                      {t('settings.overview.accountEmailLabel')}
                                    </span>
                                    <span className="fw-semibold">{account.email}</span>
                                  </CCol>
                                )}
                              </CRow>

                              <section className="mb-4">
                                <CCard className="bg-light border-0">
                                  <CCardBody>
                                    <CRow className="align-items-center g-3">
                                      <CCol xs={12} md={4}>
                                        <div className="d-flex align-items-center gap-2 text-uppercase text-muted small fw-semibold mb-2">
                                          {t('settings.reminder.title')}
                                          <CTooltip content={t('settings.reminder.tooltip')}>
                                            <CButton
                                              type="button"
                                              color="info"
                                              variant="ghost"
                                              size="sm"
                                              className="p-0 rounded-circle"
                                            >
                                              <CIcon icon={cilInfo} size="sm" />
                                            </CButton>
                                          </CTooltip>
                                        </div>
                                        <div className="display-6 fw-bold text-primary">
                                          {String(timerHours).padStart(2, '0')}:
                                          {String(timerMinutes).padStart(2, '0')}
                                        </div>
                                        <small className="text-medium-emphasis">
                                          {t('settings.reminder.description')}
                                        </small>
                                      </CCol>
                                      <CCol xs={12} md={8}>
                                        <CRow className="gy-3 gx-4">
                                          <CCol xs={12} sm={6}>
                                            <CCard className="border-0 shadow-sm h-100">
                                              <CCardBody className="d-flex flex-column align-items-center">
                                                <div className="text-uppercase small text-muted mb-2">
                                                  {t('settings.reminder.hours')}
                                                </div>
                                                <div className="d-flex align-items-center gap-3 mb-2">
                                                  <CButton
                                                    color="info"
                                                    variant="outline"
                                                    onClick={decrementHours}
                                                    shape="rounded-pill"
                                                  >
                                                    <CIcon icon={cilMinus} size="lg" />
                                                  </CButton>
                                                  <span
                                                    className="fs-2 fw-semibold"
                                                    style={{
                                                      minWidth: '3rem',
                                                      textAlign: 'center',
                                                    }}
                                                  >
                                                    {String(timerHours).padStart(2, '0')}
                                                  </span>
                                                  <CButton
                                                    color="info"
                                                    variant="outline"
                                                    onClick={incrementHours}
                                                    shape="rounded-pill"
                                                  >
                                                    <CIcon icon={cilPlus} size="lg" />
                                                  </CButton>
                                                </div>
                                              </CCardBody>
                                            </CCard>
                                          </CCol>
                                          <CCol xs={12} sm={6}>
                                            <CCard className="border-0 shadow-sm h-100">
                                              <CCardBody className="d-flex flex-column align-items-center">
                                                <div className="text-uppercase small text-muted mb-2">
                                                  {t('settings.reminder.minutes')}
                                                </div>
                                                <div className="d-flex align-items-center gap-3 mb-2">
                                                  <CButton
                                                    color="info"
                                                    variant="outline"
                                                    onClick={decrementMinutes}
                                                    shape="rounded-pill"
                                                  >
                                                    <CIcon icon={cilMinus} size="lg" />
                                                  </CButton>
                                                  <span
                                                    className="fs-2 fw-semibold"
                                                    style={{
                                                      minWidth: '3rem',
                                                      textAlign: 'center',
                                                    }}
                                                  >
                                                    {String(timerMinutes).padStart(2, '0')}
                                                  </span>
                                                  <CButton
                                                    color="info"
                                                    variant="outline"
                                                    onClick={incrementMinutes}
                                                    shape="rounded-pill"
                                                  >
                                                    <CIcon icon={cilPlus} size="lg" />
                                                  </CButton>
                                                </div>
                                              </CCardBody>
                                            </CCard>
                                          </CCol>
                                        </CRow>
                                      </CCol>
                                    </CRow>
                                  </CCardBody>
                                </CCard>
                              </section>

                              {/*
                    <section className="mb-4">
                      <CAlert color="light" className="mb-0 text-medium-emphasis">
                        Additional general settings will appear here as they become available.
                      </CAlert>
                    </section>
                    */}
                              <section>
                                <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between gap-3 mb-3">
                                  <div>
                                    <h5 className="mb-1">{t('settings.areas.title')}</h5>
                                    <span className="text-medium-emphasis small">
                                      {t('settings.areas.description')}
                                    </span>
                                  </div>
                                  <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
                                    <div
                                      className="btn-group"
                                      role="group"
                                      aria-label={t('settings.areas.filters.groupLabel')}
                                    >
                                      {filterOptions.map((option) => (
                                        <CButton
                                          key={option.id}
                                          color={aoiFilter === option.id ? 'info' : 'secondary'}
                                          variant={aoiFilter === option.id ? 'ghost' : 'outline'}
                                          onClick={() => setAoiFilter(option.id)}
                                        >
                                          {option.label}
                                        </CButton>
                                      ))}
                                    </div>
                                    <div className="text-medium-emphasis small text-end">
                                      <span className="me-3">
                                        <Trans
                                          i18nKey="settings.areas.filters.summary.active"
                                          values={{ count: summary.enabled }}
                                          components={{ strong: <strong /> }}
                                        />
                                      </span>
                                      <span className="me-3">
                                        <Trans
                                          i18nKey="settings.areas.filters.summary.disabled"
                                          values={{ count: summary.disabled }}
                                          components={{ strong: <strong /> }}
                                        />
                                      </span>
                                      <span>
                                        <Trans
                                          i18nKey="settings.areas.filters.summary.pending"
                                          values={{ count: summary.unknown }}
                                          components={{ strong: <strong /> }}
                                        />
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <CRow className="mb-3">
                                  <CCol xs={12} md={6} lg={4}>
                                    <CFormInput
                                      value={searchTerm}
                                      onChange={(event) => setSearchTerm(event.target.value)}
                                      placeholder={t('settings.areas.searchPlaceholder')}
                                      aria-label={t('settings.areas.searchAria')}
                                    />
                                  </CCol>
                                </CRow>

                                <CAccordion alwaysOpen className="mb-3">
                                  {categoryOrder.map((categoryKey, catIndex) => {
                                    const categoryItems = groupedByCategory[categoryKey] || []
                                    const visibleItems = categoryItems
                                      .filter((item) => {
                                        if (aoiFilter === 'all') return true
                                        if (aoiFilter === 'active') return item.status === 'enabled'
                                        if (aoiFilter === 'inactive')
                                          return item.status !== 'enabled'
                                        return true
                                      })
                                      .filter((item) => {
                                        if (!normalizedSearchTerm) {
                                          return true
                                        }
                                        return (item.label ?? '')
                                          .toLowerCase()
                                          .includes(normalizedSearchTerm)
                                      })

                                    if (visibleItems.length === 0) {
                                      return null
                                    }

                                    anyVisibleCategory = true

                                    const enabledCount = visibleItems.filter(
                                      (item) => item.status === 'enabled',
                                    ).length

                                    const categoryLabel = t(`settings.categories.${categoryKey}`, {
                                      defaultValue: t('settings.categories.other'),
                                    })

                                    return (
                                      <CAccordionItem
                                        itemKey={`${accountKey}-${catIndex}`}
                                        key={`${accountKey}-${categoryKey}`}
                                      >
                                        <CAccordionHeader>
                                          <span className="me-2">{categoryLabel}</span>
                                          <span className="text-medium-emphasis small">
                                            {t('settings.areas.categorySummary', {
                                              enabled: enabledCount,
                                              total: visibleItems.length,
                                            })}
                                          </span>
                                        </CAccordionHeader>
                                        <CAccordionBody>
                                          {visibleItems.map((aoi) => (
                                            <WidgetSetAOI
                                              key={aoi.key}
                                              label={aoi.label}
                                              img={aoi.img}
                                              aoiKey={aoi.aoiKey}
                                              ageKey={aoi.ageKey}
                                              configEndpoint={aoi.configEndpoint}
                                              ageEndpoint={aoi.ageEndpoint}
                                              aoiName={aoi.aoiName}
                                              onRefresh={handleRefresh}
                                            />
                                          ))}
                                        </CAccordionBody>
                                      </CAccordionItem>
                                    )
                                  })}
                                </CAccordion>
                                {!anyVisibleCategory && (
                                  <CAlert color="light" className="mb-0 text-medium-emphasis">
                                    {t('settings.areas.emptyState')}
                                  </CAlert>
                                )}
                              </section>
                            </CCardBody>
                          </CCard>
                        )
                      })
                    ) : (
                      <CAlert color="info" className="border-0 shadow-sm mb-0">
                        {t('settings.accounts.emptyState')}
                      </CAlert>
                    )}
                  </>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Settings
